import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { ZegoUIKitPrebuilt } from "@zegocloud/zego-uikit-prebuilt";
import {
  FaArrowLeft,
  FaUserInjured,
  FaClock,
  FaPhoneSlash,
  FaVideo,
  FaLock,
  FaClipboard,
  FaCalendarAlt,
  FaRupeeSign,
  FaComments,
  FaRedo,
  FaFileMedical,
} from "react-icons/fa";
import { buildKitToken, getRoomId, ZEGO_ROLE_PREFIX } from "./Zegocloud";
import { joinRoom, endRoom, getRoomDetails, hasOpenSession } from "./ZegoApi";

const BACK_PATH = "/doctor/appointments";

/* ---------- helpers ---------- */

const getDoctor = () => {
  const id = localStorage.getItem("doctorId");
  let name = localStorage.getItem("doctorName") || "";
  if (!name) {
    try {
      const u = JSON.parse(localStorage.getItem("doctor") || localStorage.getItem("user") || "{}");
      const d = u?.data || u;
      name = d.name || d.fullName || "";
    } catch (_) {}
  }
  return {
    id: id || `guest_${Date.now()}`,
    name: String(name || "Doctor"),
  };
};

const formatTime = (total) => {
  const h = Math.floor(total / 3600);
  const m = String(Math.floor((total % 3600) / 60)).padStart(2, "0");
  const s = String(total % 60).padStart(2, "0");
  return h > 0 ? `${h}:${m}:${s}` : `${m}:${s}`;
};

const formatDate = (d) => {
  if (!d) return "";
  const date = new Date(d);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
};

const InfoRow = ({ icon: Icon, label, value }) => (
  <div className="flex items-start justify-between gap-3 py-2.5 border-b border-gray-100 last:border-0">
    <span className="flex items-center gap-2 text-sm font-semibold text-gray-700 shrink-0">
      <Icon className="text-blue-600 shrink-0" size={14} />
      {label}
    </span>
    <span className="text-sm text-gray-500 text-right break-words min-w-0">{value || "—"}</span>
  </div>
);

/* ---------- component ---------- */

const DoctorVideoCall = () => {
  const { bookingId } = useParams();
  const navigate = useNavigate();
  const { state } = useLocation();

  const containerRef = useRef(null);
  const zpRef = useRef(null);
  const runIdRef = useRef(0);
  const activeRef = useRef(true);
  const navigateRef = useRef(navigate);
  const joinedRef = useRef(false); // Zego room actually joined in this run
  const endedRef = useRef(false); // endroom already sent for this run

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [joined, setJoined] = useState(false);
  const [patientPresent, setPatientPresent] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const [attempt, setAttempt] = useState(0);
  const [details, setDetails] = useState(null); // /zego/roomdetails response

  // The "patient" in this system is the staff member who booked (details.patient can be null)
  const patient = details?.patient || details?.staff || state?.patient;
  // API booking data wins; router state fills gaps (reason, etc.)
  const appointment = {
    ...(state?.appointment || {}),
    ...(details?.booking || {}),
    staffId: state?.appointment?.staffId || details?.staff?._id,
    doctor_id: state?.appointment?.doctor_id || details?.doctor?._id,
  };

  useEffect(() => {
    navigateRef.current = navigate;
  }, [navigate]);

  /* ---- room details ---- */
  const loadDetails = useCallback(async () => {
    if (!bookingId) return null;
    try {
      const res = await getRoomDetails(bookingId);
      setDetails(res);
      return res;
    } catch (e) {
      console.error("Room details error:", e);
      return null;
    }
  }, [bookingId]);

  useEffect(() => {
    loadDetails();
  }, [loadDetails]);

  /* ---- end-room (runs once per join) ---- */
  const finishCall = useCallback(async () => {
    if (endedRef.current || !joinedRef.current) return;
    endedRef.current = true;
    try {
      await endRoom(bookingId);
    } catch (e) {
      console.error("End room error:", e);
    }
  }, [bookingId]);

  // tab close / refresh / hard navigation: fire-and-forget with keepalive
  useEffect(() => {
    const onPageHide = () => {
      if (!joinedRef.current || endedRef.current) return;
      endedRef.current = true;
      endRoom(bookingId, { keepalive: true }).catch(() => {});
    };
    window.addEventListener("pagehide", onPageHide);
    return () => window.removeEventListener("pagehide", onPageHide);
  }, [bookingId]);

  // call timer
  useEffect(() => {
    if (!joined) return undefined;
    const t = setInterval(() => setSeconds((s) => s + 1), 1000);
    return () => clearInterval(t);
  }, [joined]);

  const destroyCall = useCallback(() => {
    try {
      zpRef.current?.destroy();
    } catch (_) {}
    zpRef.current = null;
  }, []);

  const endCall = useCallback(async () => {
    activeRef.current = false;
    destroyCall();
    setJoined(false);
    await finishCall();
    navigateRef.current(BACK_PATH, { replace: true });
  }, [destroyCall, finishCall]);

  useEffect(() => {
    activeRef.current = true;
    joinedRef.current = false;
    endedRef.current = false;
    const runId = ++runIdRef.current;

    const start = async () => {
      setLoading(true);
      setError("");
      setJoined(false);
      setPatientPresent(false);
      setSeconds(0);

      try {
        if (!bookingId) throw new Error("Booking ID is missing.");

        const doctor = getDoctor();
        const roomId = getRoomId(bookingId);
        const userId = `${ZEGO_ROLE_PREFIX.doctor}${doctor.id}`;
        console.log("ZEGO ROOM:", roomId, "| USER:", userId);

        const kitToken = await buildKitToken({
          roomId,
          userId,
          userName: `Dr. ${doctor.name.replace(/^Dr\.?\s*/i, "")}`,
        });

        if (runId !== runIdRef.current || !activeRef.current || !containerRef.current) return;

        const zp = ZegoUIKitPrebuilt.create(kitToken);
        zpRef.current = zp;

        zp.joinRoom({
          container: containerRef.current,
          scenario: { mode: ZegoUIKitPrebuilt.OneONoneCall },
          maxUsers: 2,
          showPreJoinView: true,
          turnOnCameraWhenJoining: true,
          turnOnMicrophoneWhenJoining: true,
          showScreenSharingButton: true, // share reports / prescriptions
          showTextChat: false,
          showUserList: false,
          showLeavingView: false,
          showRoomDetailsButton: false,
          onJoinRoom: () => {
            if (runId !== runIdRef.current) return;
            joinedRef.current = true;
            setJoined(true);

            // record the join on the backend, then refresh details
            joinRoom(bookingId)
              .catch((e) => console.error("Join record error:", e))
              .then(() => loadDetails())
              .then((res) => {
                if (res && runId === runIdRef.current && hasOpenSession(res, "staff")) {
                  setPatientPresent(true);
                }
              });
          },
          onUserJoin: () => {
            if (runId === runIdRef.current) setPatientPresent(true);
          },
          onUserLeave: () => {
            if (runId === runIdRef.current) setPatientPresent(false);
          },
          onLeaveRoom: () => {
            if (runId !== runIdRef.current || !activeRef.current) return;
            activeRef.current = false;
            setJoined(false);
            finishCall().finally(() => {
              navigateRef.current(BACK_PATH, { replace: true });
            });
          },
        });

        setLoading(false);
      } catch (e) {
        console.error("Zego join error:", e);
        if (runId === runIdRef.current && activeRef.current) {
          setError(e?.message || "Unable to start video consultation.");
          setLoading(false);
        }
      }
    };

    start();

    return () => {
      activeRef.current = false;
      finishCall(); // closes the session if the doctor navigates away mid-call
      runIdRef.current += 1;
      destroyCall();
    };
  }, [bookingId, attempt, destroyCall, finishCall, loadDetails]);

  const patientName =
    patient?.name || appointment?.patient_name || appointment?.staff_name || "Patient";
  const dateText = formatDate(appointment?.bookedSlot?.date || appointment?.date);
  const slotText =
    appointment?.bookedSlot?.timeSlot || appointment?.slot || appointment?.timeSlot;

  const statusLabel = !joined
    ? "Ready to join"
    : patientPresent
      ? "Patient connected"
      : "Waiting for patient";
  const statusClass =
    joined && patientPresent
      ? "bg-green-100 text-green-700"
      : joined
        ? "bg-yellow-100 text-yellow-700"
        : "bg-blue-100 text-blue-700";
  const dotClass =
    joined && patientPresent ? "bg-green-500" : joined ? "bg-yellow-500 animate-pulse" : "bg-blue-500";

  return (
    <div className="w-full p-3 sm:p-5 space-y-4 sm:space-y-6">
      {/* Gradient header banner */}
      <div className="rounded-xl bg-gradient-to-r from-blue-500 to-purple-600 text-white shadow-md px-4 py-4 sm:px-6 sm:py-5">
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="inline-flex items-center gap-2 text-xs sm:text-sm font-medium text-white/80 hover:text-white transition-colors"
            >
              <FaArrowLeft size={12} /> Back
            </button>
            <h1 className="text-xl sm:text-2xl font-bold mt-1 truncate">Video Consultation</h1>
            <p className="text-xs sm:text-sm text-white/80 mt-0.5 truncate">
              Consulting with {patientName}
            </p>
          </div>

          <div className="flex items-center gap-2 shrink-0">
            {joined && (
              <span className="hidden sm:inline-flex items-center gap-1.5 bg-white/15 backdrop-blur rounded-full px-3 py-1.5 text-sm font-semibold">
                <FaClock size={12} /> {formatTime(seconds)}
              </span>
            )}
            <button
              type="button"
              onClick={endCall}
              className="inline-flex items-center gap-2 min-h-[40px] px-3 sm:px-4 py-2 rounded-xl bg-red-500 hover:bg-red-600 text-white text-xs sm:text-sm font-semibold shadow transition-colors"
            >
              <FaPhoneSlash size={13} />
              <span className="hidden sm:inline">End Consultation</span>
            </button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[320px_minmax(0,1fr)] gap-4 sm:gap-6 items-start">
        {/* Left column */}
        <aside className="space-y-4 sm:space-y-6 order-2 lg:order-1">
          {/* Patient card */}
          <section className="bg-white rounded-xl shadow-md p-4 sm:p-5">
            <div className="flex items-center gap-3 sm:gap-4">
              <div className="h-14 w-14 rounded-full bg-blue-50 text-blue-600 flex items-center justify-center shrink-0 border-4 border-white shadow">
                <FaUserInjured size={22} />
              </div>
              <div className="min-w-0 flex-1">
                <h2 className="font-bold text-gray-800 text-base sm:text-lg leading-snug break-words">
                  {patientName}
                </h2>
                <p className="text-xs sm:text-sm text-gray-500 truncate">
                  {[patient?.age ? `${patient.age} yrs` : "", patient?.gender || ""]
                    .filter(Boolean)
                    .join(" • ") || "Patient"}
                </p>
              </div>
            </div>
          </section>

          {/* Appointment details */}
          <section className="bg-white rounded-xl shadow-md p-4 sm:p-5">
            <h2 className="font-semibold text-green-700 mb-2">Appointment Details</h2>
            <InfoRow icon={FaClipboard} label="Booking ID" value={`#${bookingId}`} />
            <InfoRow icon={FaCalendarAlt} label="Date" value={dateText} />
            <InfoRow icon={FaClock} label="Time Slot" value={slotText} />
            <InfoRow icon={FaVideo} label="Type" value={appointment?.type || "Online"} />
            {appointment?.totalPrice != null && (
              <InfoRow icon={FaRupeeSign} label="Fee" value={`₹${appointment.totalPrice}`} />
            )}
            {appointment?.reason && (
              <div className="mt-3 bg-gray-50 rounded-lg p-3">
                <p className="text-xs text-gray-500 mb-1">Reason</p>
                <p className="text-sm font-medium text-gray-700 break-words">
                  {appointment.reason}
                </p>
              </div>
            )}
          </section>

          {/* Quick actions */}
          <section className="bg-white rounded-xl shadow-md p-4 sm:p-5">
            <h2 className="font-semibold text-green-700 mb-3">Quick Actions</h2>
            <div className="grid grid-cols-1 gap-2">
              {appointment?.staffId && appointment?.doctor_id && (
                <button
                  type="button"
                  onClick={() =>
                    navigate(`/doctor/doctor/chat/${appointment.staffId}/${appointment.doctor_id}`)
                  }
                  className="min-h-[44px] flex items-center gap-3 px-4 rounded-xl border border-gray-200 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  <FaComments className="text-blue-600" /> Open chat
                </button>
              )}
              <button
                type="button"
                onClick={() => navigate(BACK_PATH)}
                className="min-h-[44px] flex items-center gap-3 px-4 rounded-xl border border-gray-200 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
              >
                <FaFileMedical className="text-blue-600" /> Upload report / prescription
              </button>
            </div>
            <p className="text-xs text-gray-400 mt-3 leading-relaxed">
              Tip: use the screen-share button inside the call to show reports to the patient.
            </p>
          </section>
        </aside>

        {/* Call area */}
        <section className="bg-white rounded-xl shadow-md overflow-hidden order-1 lg:order-2">
          <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-gray-100">
            <div className="flex items-center gap-2 min-w-0">
              <FaVideo className="text-blue-600 shrink-0" />
              <h2 className="font-semibold text-green-700 truncate">Consultation Room</h2>
            </div>
            <span className="inline-flex items-center gap-1.5 text-xs font-medium text-purple-600 shrink-0">
              <FaLock size={10} /> Secure
            </span>
          </div>

          {error ? (
            <div className="px-4 py-12 sm:py-16 text-center">
              <div className="mx-auto h-14 w-14 rounded-full bg-red-100 text-red-500 flex items-center justify-center mb-4">
                <FaVideo size={22} />
              </div>
              <p className="font-semibold text-gray-800">Couldn't start the call</p>
              <p className="text-sm text-gray-500 mt-1 mb-5 max-w-sm mx-auto break-words">{error}</p>
              <div className="flex flex-col sm:flex-row gap-2 justify-center">
                <button
                  type="button"
                  onClick={() => setAttempt((a) => a + 1)}
                  className="min-h-[44px] inline-flex items-center justify-center gap-2 px-5 py-2 rounded-xl bg-gradient-to-r from-blue-500 to-purple-600 text-white text-sm font-semibold shadow hover:opacity-90 transition"
                >
                  <FaRedo size={12} /> Try again
                </button>
                <button
                  type="button"
                  onClick={() => navigate(BACK_PATH, { replace: true })}
                  className="min-h-[44px] px-5 py-2 rounded-xl border border-gray-200 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Back to appointments
                </button>
              </div>
            </div>
          ) : (
            <div className="relative">
              {loading && (
                <div className="absolute inset-0 z-10 bg-white flex flex-col items-center justify-center gap-3">
                  <div className="h-10 w-10 rounded-full border-4 border-blue-100 border-t-purple-600 animate-spin" />
                  <p className="text-sm text-gray-500">Setting up consultation…</p>
                </div>
              )}
              {/* Zego needs an explicit height, otherwise it renders blank */}
              <div
                ref={containerRef}
                className="w-full bg-gray-100"
                style={{ height: "min(75vh, 720px)", minHeight: 420 }}
              />
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

export default DoctorVideoCall;